import IFlagsmith from 'flagsmith';
import { Flags } from 'flagsmith-nodejs';
import React from 'react';

// sort flags alphabetically to match flag-smith order
export const DefaultFlagsContext = {
  // These two are permanent flags, we use these to rollout flags
  // to our dogfood and launchpad segments.
  EnableAlphaApps: false,
  EnableBetaApps: false,

  EnableAppsApiMutations: false,
  EnableMobileFriendlyBillingForms: false,
  EnableRemoveAdvancedSettings: false,
  EnableCompaniesCrm: false,
  EnableManyCompanies: true, // Permanently enabled - no longer checks Flagsmith
  EnableCompanyDetailsMarketplaceApps: false,
  EnableNewIUHomeWelcomeBlurb: false,
  EnableSubscriptionCancellation: false,
  EnableMarketplaceAppNotificationUrl: false,
  EnableProductStoreAutomation: false,
  EnableImmersiveProductCreationFlow: false,
  EnableCustomizableLabels: false,
  EnableChatUIUpdates: false,
  EnableTagsMultiSelectUIRedesign: false,
  EnableFolderLocking: false,
  EnableCompanyCustomFieldAppVisibility: false,
  EnableCreateClientFromCompanyDetails: false,
  EnableCompanyCustomFieldURLParams: false,
  EnableSubscriptionEditSummary: false,
};

export type FlagContextType = typeof DefaultFlagsContext;

export const getServerFlagsContextValue = (flags: Flags) => ({
  EnableAlphaApps: flags.isFeatureEnabled('enable_alpha_apps'),
  EnableBetaApps: flags.isFeatureEnabled('enable_beta_apps'),
  EnableAppsApiMutations: flags.isFeatureEnabled('enable_apps_api_mutations'),
  EnableMobileFriendlyBillingForms: flags.isFeatureEnabled(
    'mobile_friendly_billing_forms',
  ),
  EnableRemoveAdvancedSettings: flags.isFeatureEnabled(
    'remove_advanced_settings',
  ),
  EnableCompaniesCrm: flags.isFeatureEnabled('enable_companies_crm'),
  EnableManyCompanies: true, // Permanently enabled - no longer checks Flagsmith
  EnableCompanyDetailsMarketplaceApps: flags.isFeatureEnabled(
    'enable_company_details_marketplace_apps',
  ),
  EnableSubscriptionCancellation: flags.isFeatureEnabled(
    'client_subscription_cancellation',
  ),
  EnableMarketplaceAppNotificationUrl: flags.isFeatureEnabled(
    'marketplace_app_notification_embeds',
  ),
  EnableProductStoreAutomation: flags.isFeatureEnabled(
    'product_store_automation',
  ),
  EnableImmersiveProductCreationFlow: flags.isFeatureEnabled(
    'immersive_product_creation_flow',
  ),
  EnableCustomizableLabels: flags.isFeatureEnabled('customizable_labels'),
  EnableChatUIUpdates: flags.isFeatureEnabled('enable_chat_ui_updates'),
  EnableTagsMultiSelectUIRedesign: flags.isFeatureEnabled(
    'enable_tags_multi_select_ui_redesign',
  ),
  EnableFolderLocking: flags.isFeatureEnabled('enable_folder_locking'),
  EnableCompanyCustomFieldAppVisibility: flags.isFeatureEnabled(
    'company_custom_field_app_visibility',
  ),
  EnableCreateClientFromCompanyDetails: flags.isFeatureEnabled(
    'enable_create_client_from_company_details',
  ),
  EnableCompanyCustomFieldURLParams: flags.isFeatureEnabled(
    'company_custom_field_url_params',
  ),
  EnableSubscriptionEditSummary: flags.isFeatureEnabled(
    'subscription_edit_summary',
  ),
});

export const getClientFlagsContextValue = (flags: typeof IFlagsmith) => ({
  EnableAlphaApps: flags.hasFeature('enable_alpha_apps'),
  EnableBetaApps: flags.hasFeature('enable_beta_apps'),
  EnableAppsApiMutations: flags.hasFeature('enable_apps_api_mutations'),
  EnableNewIUHomeWelcomeBlurb: flags.hasFeature(
    'enable_new_iu_home_welcome_blurb',
  ),
  EnableMobileFriendlyBillingForms: flags.hasFeature(
    'mobile_friendly_billing_forms',
  ),
  EnableRemoveAdvancedSettings: flags.hasFeature('remove_advanced_settings'),
  EnableCompaniesCrm: flags.hasFeature('enable_companies_crm'),
  EnableManyCompanies: true, // Permanently enabled - no longer checks Flagsmith
  EnableCompanyDetailsMarketplaceApps: flags.hasFeature(
    'enable_company_details_marketplace_apps',
  ),
  EnableSubscriptionCancellation: flags.hasFeature(
    'client_subscription_cancellation',
  ),
  EnableMarketplaceAppNotificationUrl: flags.hasFeature(
    'marketplace_app_notification_embeds',
  ),
  EnableProductStoreAutomation: flags.hasFeature('product_store_automation'),
  EnableImmersiveProductCreationFlow: flags.hasFeature(
    'immersive_product_creation_flow',
  ),
  EnableCustomizableLabels: flags.hasFeature('customizable_labels'),
  EnableChatUIUpdates: flags.hasFeature('enable_chat_ui_updates'),
  EnableTagsMultiSelectUIRedesign: flags.hasFeature(
    'enable_tags_multi_select_ui_redesign',
  ),
  EnableFolderLocking: flags.hasFeature('enable_folder_locking'),
  EnableCompanyCustomFieldAppVisibility: flags.hasFeature(
    'company_custom_field_app_visibility',
  ),
  EnableCreateClientFromCompanyDetails: flags.hasFeature(
    'enable_create_client_from_company_details',
  ),
  EnableCompanyCustomFieldURLParams: flags.hasFeature(
    'company_custom_field_url_params',
  ),
  EnableSubscriptionEditSummary: flags.hasFeature('subscription_edit_summary'),
});

export const FlagsContext = React.createContext(DefaultFlagsContext);
